import * as amplitude from "@amplitude/analytics-react-native"
import { DialogM } from "@common/components/alert/dialog/Dialog"
import { SnackBarM } from "@common/components/alert/snackbar/SnackBar"
import ImageViewer from "@common/components/image/ImageViewer"
import { ModalM } from "@common/components/modal/ModalM"
import TextM from "@common/components/text/TextM"
import { StructuredCorrectionBlock } from "@common/components/correction/StructuredCorrectionBlock"
import CorrectionMarkersOverlay from "@common/components/correction/CorrectionMarkersOverlay"
import CorrectionPointsPanel from "@common/components/correction/CorrectionPointsPanel"
import {
    anchorPageIndex,
    hasUsableAnchor,
} from "@common/components/correction/correctionPointTypes"
import IconButtonM from "@common/components/button/IconButtonM"
import TouchableRippleM from "@common/components/button/TouchableRippleM"
import FeedbackButton from "@common/components/feedback/FeedbackButton"
import useLiveKitEventSender from "@common/hooks/livekit/useLiveKitEventSender"
import { useSeenStudentActivity } from "@common/hooks/livekit/useParticipantState"
import { useApiStatus } from "@common/hooks/useApiStatus"
import useLaunchActivity from "@common/hooks/useLaunchActivity"
import { theme } from "@common/theme/theme"
import { DimensionsContext, IS_WEB } from "@common/utils/mobileUtils"
import { cleanMediaUrl } from "@data/constants/apiConstants"
import { apiIdle, apiRequest } from "@data/redux/actions/api"
import { activitiesService } from "@data/services/activitiesService"
import { liveService } from "@data/services/liveService"
import { studentsService } from "@data/services/studentsService"
import { hasRole } from "@data/utility/ability"
import { AbilityContext } from "@data/utility/Can"
import { findItemInListWithId } from "@data/utility/dataUtils"
import { buildPendingAttachmentsBody } from "@data/utility/pendingAttachmentsUtils"
import React, { useContext, useEffect, useMemo, useRef, useState } from "react"
import { Pressable, ScrollView, StyleSheet, View } from "react-native"
import { IconButton, Menu, Tooltip } from "react-native-paper"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { connect } from "react-redux"

import AvatarM from "../../../common/components/avatar/AvatarM"
import PrerequisiteQuizDisplay from "../../../reviewer/PrerequisiteQuizDisplay"
import { AudioRecordingContainer } from "../execute/AudioRecordingContainer"
import MaterialModal from "../execute/MaterialDisplayModal"
import ScreenShareToggleButton from "../../../common/liveMode/liveV3/ScreenShareToggleButton"
import AnnotationCanvas from "../../../common/liveMode/liveV3/AnnotationCanvas"
import EditActivityTitleModal from "./EditActivityTitleModal"
import ToolbarButton from "./components/ToolbarButton"
import ShowToStudentButton, { useShowToStudentLoading } from "./components/ShowToStudentButton"
import WhiteboardTab from "./components/WhiteboardTab"
import ParcoursChoiceModal from "./components/ParcoursChoiceModal"
import ActivityItemInfo from "./components/ActivityItemInfo"
import QuizFirstMention from "./components/QuizFirstMention"
import { useAnnotationState } from "../../../common/hooks/useAnnotationState"
import { useCorrectionPolling } from "../../../engine/activity/correction/useCorrectionPolling"

export const canEditActivity = (activity, ability, isParentMode = false) =>
    ability?.can("assign", "activities") ||
    activity?.created_by?.id === activity?.student_id ||
    isParentMode

// La même modale est ouverte depuis l'espace parent : l'élève est tutoyé, le
// parent est vouvoyé.
const TEXTS = {
    student: {
        deleteDenied:
            "L'activité a été créée par le tuteur, tu ne peux pas la supprimer.",
        updateDenied:
            "L'activité a été créée par le tuteur, tu ne peux pas la modifier.",
        archiveTitle: "Veux-tu archiver cette activité ?",
        archiveHint: "Tu pourras la restaurer depuis les activités archivées.",
    },
    parent: {
        deleteDenied:
            "L'activité a été créée par le tuteur, elle ne peut pas être supprimée.",
        updateDenied:
            "L'activité a été créée par le tuteur, elle ne peut pas être modifiée.",
        archiveTitle: "Voulez-vous archiver cette activité ?",
        archiveHint: "Elle pourra être restaurée depuis les activités archivées.",
    },
}

const TodoModal = (props) => {
    const {
        isOpen,
        handleShowModal = () => {},
        item,
        studentId = null,
        student = null,
        schoolSubject,
        width = null,
        height = null,
        noModal = false,
        onExit = null,
        onRequestClose = null,
        navigation,
        route,
        interviewMode = false,
        fromArchived = false,
        // Draft preview mode — Sam Prescription embeds TodoModal with a
        // draft (Material only, no persisted Activity id). When true:
        //   • skip API refreshes / correction polling tied to item.id
        //   • disable destructive / activity-id-dependent actions
        //   • keep visual rendering so the tutor sees the draft as if it
        //     were a normal activity. The "Générer..." entry point is
        //     gated off because Sam wires its own refresh button on the
        //     student card instead.
        draftMode = false,
    } = props
    const dimensions = useContext(DimensionsContext)

    const [isMaterialOpen, setMaterialOpen] = useState(false)
    const handleShowMaterialModal = () => setMaterialOpen(!isMaterialOpen)

    const [showParcoursChoice, setShowParcoursChoice] = useState(false)
    const [material, setMaterial] = useState(item?.material)
    const [menuVisible, setMenuVisible] = useState(false)
    const [isTitleEditModalVisible, setTitleEditModalVisible] = useState(false)
    const prevItemIdRef = useRef(item?.id)

    const activityId = item?.id
    const shareableRef = useRef(null) // ref for Region Capture crop zone (tabs + content)
    const screenShareToggleRef = useRef(null)

    // Focus screen share / annotation state
    const isFocusShareActive = props.isFocusScreenShare && noModal

    // Annotation state (drawing, strokes, canvases)
    const {
        isDrawing,
        setIsDrawing,
        strokeCount,
        setStrokeCount,
        contentLayout,
        setContentLayout,
        materialCanvasRef,
        imageActiveCanvasRef,
        lastActiveCanvasRef,
        whiteboardCanvasRef,
        getStrokesRef,
        getImageStrokesRef,
        getActivityStrokeCount,
    } = useAnnotationState(activityId, isFocusShareActive)

    const [shareHintDismissed, setShareHintDismissed] = useState(false)

    // Reset hint when screen share becomes active
    useEffect(() => {
        if (isFocusShareActive) setShareHintDismissed(false)
    }, [isFocusShareActive])

    const [quizFeedback, setQuizFeedback] = useState(null)

    const refresh = () => {
        if (interviewMode) {
            return
        }
        if (draftMode || !item?.id) {
            // A Sam draft has no persisted activity yet — skip the
            // activity-list refresh that would 404 on an undefined id.
            return
        }

        props.apiRequest(
            activitiesService.getActivities,
            {},
            {},
            {
                with_logs: 1,
                activity_ids: item?.id,
                include_archived: fromArchived ? 1 : 0,
            }
        )
    }

    // Polling for correction generation — skip in draft mode (no
    // persisted activity to poll).
    const shouldPollCorrection =
        !draftMode &&
        (item?.status === "pending_review" || item?.correction_generating)
    useCorrectionPolling(shouldPollCorrection, refresh)

    // --- Tutor feedback on the structured correction (feature_type='correction').
    // Reuses the generic Feedback mechanism; the tutor's row is keyed by user, so
    // it is distinct from the student's. Student-side display is a separate surface.
    const isTutorViewer = hasRole(props.user, "tutor")
    const [correctionFeedbackType, setCorrectionFeedbackType] = useState(null)
    const [correctionFeedbackSubmitting, setCorrectionFeedbackSubmitting] =
        useState(false)

    // Marker ↔ card sync: the selected point is mirrored on the copy's
    // interactive bubbles AND on the analysis cards; the eye toggle hides the
    // bubbles to read the raw copy (annotations never occlude the answer).
    const [selectedPointIndex, setSelectedPointIndex] = useState(null)
    const [annotationsVisible, setAnnotationsVisible] = useState(true)
    const [viewerImageIndex, setViewerImageIndex] = useState(0)
    const analysisScrollRef = useRef(null)
    const cardLayoutsRef = useRef({})
    // Measured height of the display-mode content area — caps the points
    // panel to ~35% of it so the copy always keeps the vertical majority,
    // whatever the window height (the card's explication scrolls inside).
    const [modeContentHeight, setModeContentHeight] = useState(null)
    const pointsPanelHeight = modeContentHeight
        ? Math.min(270, Math.round(modeContentHeight * 0.35))
        : 270

    // Selection resets when the displayed activity changes.
    useEffect(() => {
        setSelectedPointIndex(null)
        setViewerImageIndex(0)
        cardLayoutsRef.current = {}
    }, [item?.id])

    useEffect(() => {
        if (isTutorViewer && item?.id && item?.correction_status === "completed") {
            props.apiRequest(activitiesService.getActivityCorrectionFeedback, {
                activityId: item.id,
            })
        } else {
            setCorrectionFeedbackType(null)
        }
    }, [isTutorViewer, item?.id, item?.correction_status])

    useApiStatus(
        activitiesService.getActivityCorrectionFeedback,
        null,
        false,
        (data) => setCorrectionFeedbackType(data?.feedback_type ?? null)
    )

    useApiStatus(
        activitiesService.submitActivityCorrectionFeedback,
        null,
        true,
        () => setCorrectionFeedbackSubmitting(false),
        () => setCorrectionFeedbackSubmitting(false)
    )

    const handleCorrectionFeedback = (type) => {
        if (!item?.id || correctionFeedbackSubmitting) return
        setCorrectionFeedbackSubmitting(true)
        setCorrectionFeedbackType(type)
        props.apiRequest(
            activitiesService.submitActivityCorrectionFeedback,
            {},
            { activityId: item.id, feedbackType: type }
        )
    }

    // `compact` drops the question label and outer spacing — for the bottom
    // panel's bar, where the two buttons sit next to the navigation arrows.
    const renderCorrectionFeedback = (compact = false) => (
        <View
            style={
                compact
                    ? {
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 8,
                      }
                    : {
                          flexDirection: "row",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: 12,
                          marginTop: 12,
                      }
            }
        >
            {!compact && (
                <TextM fontSize={12} color={theme.colors.grey} wrap>
                    Cette correction est-elle juste ?
                </TextM>
            )}
            <FeedbackButton
                isPositive
                isSelected={correctionFeedbackType === "positive"}
                label="Oui"
                tooltipText="Valider la correction"
                disabled={correctionFeedbackSubmitting}
                onPress={() => handleCorrectionFeedback("positive")}
            />
            <FeedbackButton
                isPositive={false}
                isSelected={correctionFeedbackType === "negative"}
                label="À revoir"
                tooltipText="Signaler un point faux"
                disabled={correctionFeedbackSubmitting}
                onPress={() => handleCorrectionFeedback("negative")}
            />
        </View>
    )

    // Scrollable analysis panel (typed points + feedback). Shared by the
    // "Rendu + correction" side-by-side layout and the "Énoncé + Rendu" stacked
    // layout so the two stay in sync.

    // Scroll the analysis list to a point's card (offsets reported by the
    // block via onCardLayout, relative to the block root — the list's only
    // child besides padding).
    const scrollToCard = (index) => {
        const y = cardLayoutsRef.current?.[index]
        if (typeof y === "number") {
            analysisScrollRef.current?.scrollTo({
                y: Math.max(0, y),
                animated: true,
            })
        }
    }

    // Tap on a copy bubble → select + bring the matching card into view.
    const handleSelectPointFromMarker = (index) => {
        setSelectedPointIndex(index)
        if (index != null) scrollToCard(index)
    }

    // Tap on a card → select + bring the point's copy page into view.
    const handleSelectPointFromCard = (index) => {
        setSelectedPointIndex(index)
        if (index == null) return
        const point = correctionPoints?.find((p) => p?.index === index)
        if (point && hasUsableAnchor(point)) {
            setViewerImageIndex(anchorPageIndex(point, workImages?.length))
        }
    }

    const renderCorrectionAnalysisPanel = () => (
        <View style={{ flex: 1, minHeight: 0 }}>
            <ScrollView
                ref={analysisScrollRef}
                style={{ flex: 1 }}
                contentContainerStyle={{
                    paddingHorizontal: 12,
                    paddingBottom: 12,
                }}
            >
                <StructuredCorrectionBlock
                    points={correctionPoints}
                    title="Analyse de la copie"
                    selectedIndex={selectedPointIndex}
                    onSelectPoint={handleSelectPointFromCard}
                    onCardLayout={(index, y) => {
                        cardLayoutsRef.current[index] = y
                    }}
                />
            </ScrollView>
            {/* Sticky footer: the tutor's feedback action stays reachable
                without scrolling through the whole analysis. */}
            <View
                style={{
                    borderTopWidth: 1,
                    borderTopColor: theme.colors.lightMore,
                    paddingHorizontal: 12,
                    paddingBottom: 10,
                }}
            >
                {renderCorrectionFeedback()}
            </View>
        </View>
    )

    const ability = useContext(AbilityContext)

    const canEdit = canEditActivity(item, ability, props.isParentMode)

    const texts = TEXTS[props.isParentMode ? "parent" : "student"]

    const [pendingAction, setPendingAction] = useState(null)

    const isOnline = !!props.participants?.find((x) => x === student?.id)
    // What the student really has in front of them, not merely open (hidden
    // under the visio on a phone, the avatar here contradicted the student).
    const seenStudentActivityId = useSeenStudentActivity(student?.id)
    const currentStudentActivityId = isOnline ? seenStudentActivityId : null

    // The loader after "Afficher à l'élève", stopped as soon as the student
    // has the activity in front of them.
    const afficher = useShowToStudentLoading(currentStudentActivityId, item?.id)

    const onDeletePress = () => {
        if (!canEdit) {
            handleShowModal()
            DialogM.show({
                text1: "Non autorisé",
                text2: texts.deleteDenied,
            })
            return
        }
        setPendingAction("delete")
        props.apiRequest(activitiesService.getActivity, { id: item?.id })
    }
    const onUpdatePress = () => {
        if (!canEdit) {
            handleShowModal()
            DialogM.show({
                text1: "Non autorisé",
                text2: texts.updateDenied,
            })
            return
        }
        setPendingAction("update")
        props.apiRequest(activitiesService.getActivity, { id: item?.id })
    }
    const onRestorePress = () => {
        // Directly restore without needing getActivity
        props.apiRequest(
            activitiesService.restoreArchivedActivities,
            { student: studentId },
            { activity_ids: [item?.id] }
        )
    }
    useEffect(() => {
        // Pas de garde `draftMode` ici : `refresh()` porte déjà la sienne, et
        // la dupliquer laissait deux endroits où l'oublier.
        refresh()
    }, [item?.media_count, item?.id])

    // Close modal if activity no longer exists (e.g., deleted via multi-delete)
    useEffect(() => {
        if (draftMode) return
        if (!item?.id || !student || fromArchived) return

        const activityExists = student?.todo_activities
            ?.concat(student?.activities || [])
            ?.some((activity) => activity?.id === item?.id)

        if (!activityExists && onExit) {
            onExit()
        }
    }, [
        student?.todo_activities,
        student?.activities,
        item?.id,
        onExit,
        fromArchived,
        draftMode,
    ])

    const onDeleteTodo =
        props?.onDeleteTodo ||
        ((todoId) => {
            DialogM.show({
                text1: texts.archiveTitle,
                text2: texts.archiveHint,
                buttonText1: "Archiver",
                buttonColor1: theme.colors.primary,
                onPress: () => {
                    props.apiRequest(
                        activitiesService.deleteActivitiesTodo,
                        {
                            student: studentId,
                        },
                        { activity_ids: [todoId] }
                    )
                },
                buttonText2: "Annuler ",
            })
        })

    const { sendLiveKitData: updateTodo } = useLiveKitEventSender()

    useApiStatus(
        activitiesService.deleteActivitiesTodo, // api service
        null, // success message on toast (string or null)
        true, // error message on toast (true) or keep it in redux state (false)
        (data) => {
            if (studentId) {
                updateTodo(
                    "deleteTodo",
                    {
                        id: studentId,
                        activity_ids: data?.deleted_activity_ids || [],
                    },
                    [studentId]
                )
            }
            if (onExit) onExit()
            else handleShowModal()
        } // success callback
    )

    useApiStatus(
        activitiesService.getActivity, // api service
        null, // success message on toast (string or null)
        true, // error message on toast (true) or keep it in redux state (false)
        (data) => {
            handleShowModal()
            if (data?.locked_at && data?.student_id !== props.user?.id) {
                DialogM.show({
                    text1: "Impossible",
                    text2: "L'élève a actuellement ouvert cette activité.",
                })
            } else {
                if (pendingAction === "delete") {
                    onDeleteTodo(item?.id)
                } else if (pendingAction === "update") {
                    navigation?.navigate("new-todo", {
                        todoId: item?.id,
                        studentId: studentId,
                    })
                }
            }
            setPendingAction(null)
        },
        () => {
            handleShowModal()
            setPendingAction(null)
        }
    )

    useApiStatus(
        activitiesService.restoreArchivedActivities,
        null,
        true,
        () => {
            if (hasRole(props.user, "tutor") && studentId) {
                props.apiRequest(studentsService.getStudent, { id: studentId })
            }
            handleShowModal()
        }
    )

    useApiStatus(
        activitiesService.getActivities, // api service
        null, // success message on toast (string or null)
        true, // error message on toast (true) or keep it in redux state (false)
        () => {} // success callback
    )

    useApiStatus(
        activitiesService.finalizeActivitySilent, // api service
        null, // success message on toast (string or null)
        true, // error message on toast (true) or keep it in redux state (false)
        (successData) => {
            // Calculate rewards for display
            const skillSets = successData?.skill_sets || []
            const petals =
                skillSets
                    ?.filter(
                        (ss) =>
                            ss?.current_count === 0 &&
                            !ss?.is_last_evolution_read
                    )
                    ?.reduce(
                        (sum, ss) =>
                            sum +
                            (ss?.steps?.[ss?.current_step - 1]?.petals_gained ||
                                0),
                        0
                    ) || 0
            const xpGained = successData?.activity?.xp_gained ?? 0

            // Show tutor snackbar
            SnackBarM.show({
                text1: `✅ Activité soumise pour ${
                    student?.firstname || "l'élève"
                }`,
                duration: 4000,
            })

            // Refresh student data on tutor side
            if (studentId) {
                props.apiRequest(studentsService.getStudent, { id: studentId })
            }

            // Notify student via LiveKit with rewards and activity data
            if (props.isLiveMode && studentId) {
                const finalizedActivity = successData?.activity
                updateTodo(
                    "tutorFinalizedActivity",
                    {
                        studentId: studentId,
                        activityId: item?.id,
                        activityTitle: item?.material?.title ?? item?.title,
                        xpGained: xpGained,
                        petals: petals,
                        // Activity data for proper display on student side
                        finishedAt: finalizedActivity?.finished_at,
                        status: finalizedActivity?.status, // Use actual status from API (pending_review for AI scoring, not_reviewed otherwise)
                        duration: finalizedActivity?.duration,
                        xpGainedActivity: finalizedActivity?.xp_gained,
                    },
                    [studentId]
                )
            }

            refresh()
        } // success callback
    )

    const requestApiService = hasRole(props.user, "student")
        ? activitiesService.activityTodoAsStudent
        : activitiesService.activityTodoAsTutor

    const launchActivity = useLaunchActivity(navigation, route)

    useApiStatus(
        requestApiService, // api service
        null, // success message on toast (string or null)
        true, // error message on toast (true) or keep it in redux state (false)
        (data) => {
            if (hasRole(props.user, "student") && !props.isParentMode) {
                launchActivity(
                    {
                        activity_template_id: activityTemplate?.id,
                        id: item?.id,
                        school_subject_id: schoolSubject?.id,
                        chapterName: item?.student_chapter?.chapter?.name,
                        chapterId: item?.student_chapter?.id,
                    },
                    "replace"
                )
            } else if (props.isParentMode) {
                SnackBarM.show({
                    text1: "✅ L'activité a été redonnée à l'élève",
                    duration: 4000,
                })
                handleShowModal()
            } else {
                SnackBarM.show({
                    text1: "✅ La même activité a été ajoutée à la liste de l'élève",
                    duration: 4000,
                })
                handleShowModal()

                updateTodo(
                    "newTodo",
                    {
                        id: studentId,
                        activity: data,
                    },
                    [studentId]
                )
            }
        } // success callback
    )

    // Quiz feedback: Initialize from existing quiz attempt data
    useEffect(() => {
        const attemptFeedback = item?.prerequisite_quiz_attempt?.tutor_feedback
        if (attemptFeedback !== undefined && attemptFeedback !== null) {
            setQuizFeedback(attemptFeedback)
        } else {
            setQuizFeedback(null)
        }
    }, [item?.prerequisite_quiz_attempt?.tutor_feedback, item?.id])

    // Quiz feedback: Handle API response
    useApiStatus(activitiesService.giveQuizFeedback, null, true, (data) => {
        if (data?.tutor_feedback !== undefined) {
            setQuizFeedback(data.tutor_feedback)
        }
    })

    // Handler for quiz feedback (tutor like/dislike)
    const handleQuizFeedback = (value) => {
        if (quizFeedback !== null) return // Already gave feedback

        if (!item?.id) return

        amplitude.track("quiz.tutor.feedback.submit", {
            quiz_id: item?.prerequisite_quiz?.id,
            activity_id: item?.id,
            feedback_value: value,
        })

        props.apiRequest(
            activitiesService.giveQuizFeedback,
            { activityId: item?.id },
            {
                feedback_value: value,
            }
        )
        setQuizFeedback(value)
    }

    const activityTemplate = item
        ? findItemInListWithId(
              item?.activity_template_id,
              props.activityTemplates
          )
        : null

    const onPress = () => {
        props.apiRequest(
            requestApiService,
            hasRole(props.user, "student") ? {} : { id: studentId },
            {
                activity_template_id: activityTemplate?.id,
                school_subject_id: schoolSubject?.id,
                material_id: item?.material?.id,
                material_chapter: item?.chapter,
                estimated_duration: item?.estimated_duration,
                chapter_id: item?.student_chapter?.chapter?.id,
                chapter_name: item?.student_chapter?.chapter?.name,
                chapter_class: item?.student_chapter?.chapter?.class,
            }
        )
    }

    // In draft mode there's no persisted activity yet — started_at /
    // locked_at are undefined (not null), which the !== null comparison
    // below would otherwise misread as "ongoing", showing a green
    // status chip in the header. Force null so no status badge renders.
    const status = draftMode
        ? null
        : item?.finished_at
            ? null
            : item?.started_at !== null && item?.locked_at !== null
              ? "ongoing"
              : item?.started_at !== null && item?.locked_at === null
                ? "started"
                : null

    const statusActivity = draftMode ? null : item?.status

    const handleValidateActivity = (item) => {
        props.apiRequest(activitiesService.tutorValidateActivity, {
            id: item.id,
            reviewer: props.user?.id,
        })
    }

    useApiStatus(
        activitiesService.tutorValidateActivity, // api service
        null, // success message on toast (string or null)
        true, // error message on toast (true) or keep it in redux state (false)
        () => {
            refresh()
        }
    )

    const showDialogValiderManuellement = (item) => {
        DialogM.show({
            text1: "Valider l'activité",
            text2: "Veux-tu marquer cette activité comme validée ?",
            buttonText1: "Valider",
            onPress: () => {
                handleValidateActivity(item)
            },
            buttonText2: "Retour",
            onPressBack: () => {},
        })
    }

    const sendAfficherEvent = () => {
        afficher.start()
        updateTodo(
            "newTodo",
            {
                id: student?.id,
                activity: item,
                forceFocus: true,
            },
            [student?.id]
        ).then((result) => {
            // No tutor-facing message on a confirmed failure either: these
            // reconnects are frequent enough (see issue occurrences) that a
            // message here would read as noise more often than as signal,
            // and without an applicative ack there's no way to tell "lost"
            // from "will succeed on its own in a moment". Bugsnag is the
            // measurement; the retry above is the mitigation. Revisit once
            // SC4 (the ack) lands.
            if (result && result.ok === false) afficher.stop()
        })
    }

    const handleAfficherActivity = () => {
        const isInCheckpoint = props.ongoingCheckpointIds?.includes(student?.id)

        // Displaying an activity closes the student's conversation with Sam,
        // the one they're on included: ask first, whichever it is.
        if (isInCheckpoint) {
            DialogM.show({
                champion: "sam",
                variant: "main",
                text1: `${student?.display_name} est en plein point d'avancement avec Sam`,
                text2: "Souhaites-tu interrompre la conversation pour afficher cette activité ?",
                buttonText1: "Oui, interrompre",
                buttonColor1: theme.colors.primary,
                onPress: () => {
                    amplitude.track("checkpoint.interrupt.accept")

                    const activeConversation =
                        student?.conversations?.[
                            student?.conversations?.length - 1
                        ]

                    if (activeConversation?.id) {
                        props.apiRequest(
                            liveService.finishConversation,
                            { conversation_id: activeConversation.id },
                            {
                                interruption_reason: "tutor_interruption",
                                ended_at: new Date().toISOString(),
                            }
                        )
                    }

                    sendAfficherEvent()
                },
                buttonText2: "Non",
                onPress2: () => {
                    amplitude.track("checkpoint.interrupt.reject")
                },
            })
        } else {
            sendAfficherEvent()
        }
    }

    // Render action buttons for header (toolbar style)
    const renderHeaderActions = () => {
        const actions = []

        // Afficher à l'élève (if student connected): it brings the activity
        // back in front of them, or says it already is (ShowToStudentButton).
        // Skip in draft mode — there's no persisted Activity id to display
        // and the preview is meant to be informational only.
        if (
            !fromArchived &&
            !draftMode &&
            student &&
            isOnline &&
            hasRole(props.user, "tutor")
        ) {
            actions.push(
                <ShowToStudentButton
                    key="afficher"
                    seenActivityId={currentStudentActivityId}
                    activityId={item?.id}
                    onShow={handleAfficherActivity}
                    loading={afficher.loading}
                    style={todoModalStyles.toolbarButton}
                />
            )
        }

        // Générer (if has material_key_skills) — NOT shown in draft mode:
        // Sam Prescription wires its own refresh icon on the student card,
        // and Wajih asked to keep that gesture outside the TodoModal so the
        // preview here stays purely informational.
        if (
            !fromArchived &&
            !draftMode &&
            hasRole(props.user, "tutor") &&
            item?.material?.material_key_skills
        ) {
            actions.push(
                <ToolbarButton
                    key="generer"
                    icon="magic"
                    label="Générer..."
                    onPress={() => setShowParcoursChoice(true)}
                    style={todoModalStyles.toolbarButton}
                />
            )
        }

        // Marquer comme faite (tutor, not finished, has media)
        if (
            hasRole(props.user, "tutor") &&
            !item?.finished_at &&
            item?.media_count > 0
        ) {
            actions.push(
                <ToolbarButton
                    key="marquer-faite"
                    icon="bolt"
                    label="Soumettre"
                    style={todoModalStyles.toolbarButton}
                    onPress={() => {
                        // Tutor submit-on-behalf: the student's media already
                        // exists as logs (gated on media_count > 0), so pending
                        // attachments are normally empty here. We still forward
                        // any un-confirmed tmp key for parity with the student
                        // finalize call-sites — no call-site should drop a tmp
                        // key it holds.
                        const body = { chrono_duration: item?.duration || 0 }
                        const pending = buildPendingAttachmentsBody(
                            props.pendingAttachmentsByActivity?.[item?.id]
                        )
                        if (pending) body.pending_attachments = pending
                        props.apiRequest(
                            activitiesService.finalizeActivitySilent,
                            { id: item?.id },
                            body
                        )
                    }}
                    loading={props.finalizeActivitySilentRequest}
                    color={theme.colors.success}
                />
            )
        }

        // Screen share: segmented group (annotation tools + stop) or standalone Présenter
        if (props.isLiveMode && hasRole(props.user, "tutor") && noModal) {
            if (isFocusShareActive) {
                actions.push(
                    <View
                        key="screen-share-group"
                        style={todoModalStyles.segmentedGroup}
                    >
                        <Tooltip
                            title={
                                isDrawing
                                    ? "Désactiver (Échap)"
                                    : "Dessiner (P)"
                            }
                            theme={{
                                colors: {
                                    onSurface: theme.colors.primary,
                                    surface: theme.colors.white,
                                },
                            }}
                        >
                            <Pressable
                                onPress={() => setIsDrawing(!isDrawing)}
                                style={({ pressed }) => [
                                    todoModalStyles.segmentedButton,
                                    isDrawing && {
                                        backgroundColor: theme.colors.primary,
                                    },
                                    { opacity: pressed ? 0.7 : 1 },
                                ]}
                            >
                                <FontAwesome5
                                    name="pen"
                                    size={14}
                                    color={
                                        isDrawing
                                            ? "#FFFFFF"
                                            : theme.colors.greyAlt
                                    }
                                    solid
                                />
                            </Pressable>
                        </Tooltip>
                        <Tooltip
                            title="Annuler (Ctrl+Z)"
                            theme={{
                                colors: {
                                    onSurface: theme.colors.primary,
                                    surface: theme.colors.white,
                                },
                            }}
                        >
                            <Pressable
                                onPress={() => {
                                    const mode =
                                        activityDisplayModes[
                                            activityDisplayModeIndex
                                        ]
                                    const canvas =
                                        mode === "visual_explanation"
                                            ? whiteboardCanvasRef.current
                                            : mode === "split"
                                              ? lastActiveCanvasRef.current
                                              : mode === "work_attachments"
                                                ? imageActiveCanvasRef.current
                                                : materialCanvasRef.current
                                    canvas?.undo()
                                    setStrokeCount(getActivityStrokeCount())
                                }}
                                disabled={strokeCount === 0}
                                style={({ pressed }) => [
                                    todoModalStyles.segmentedButton,
                                    {
                                        opacity:
                                            strokeCount === 0
                                                ? 0.3
                                                : pressed
                                                  ? 0.7
                                                  : 1,
                                    },
                                ]}
                            >
                                <FontAwesome5
                                    name="undo"
                                    size={14}
                                    color={theme.colors.greyAlt}
                                    solid
                                />
                            </Pressable>
                        </Tooltip>
                        <Tooltip
                            title="Tout effacer (Suppr)"
                            theme={{
                                colors: {
                                    onSurface: theme.colors.primary,
                                    surface: theme.colors.white,
                                },
                            }}
                        >
                            <Pressable
                                onPress={() => {
                                    const mode =
                                        activityDisplayModes[
                                            activityDisplayModeIndex
                                        ]
                                    if (mode === "visual_explanation") {
                                        whiteboardCanvasRef.current?.clearAll()
                                    } else if (mode === "split") {
                                        materialCanvasRef.current?.clearAll()
                                        imageActiveCanvasRef.current?.clearAll()
                                    } else if (mode === "work_attachments") {
                                        imageActiveCanvasRef.current?.clearAll()
                                    } else {
                                        materialCanvasRef.current?.clearAll()
                                    }
                                    setStrokeCount(getActivityStrokeCount())
                                }}
                                disabled={strokeCount === 0}
                                style={({ pressed }) => [
                                    todoModalStyles.segmentedButton,
                                    {
                                        opacity:
                                            strokeCount === 0
                                                ? 0.3
                                                : pressed
                                                  ? 0.7
                                                  : 1,
                                    },
                                ]}
                            >
                                <FontAwesome5
                                    name="trash"
                                    size={14}
                                    color={theme.colors.greyAlt}
                                    solid
                                />
                            </Pressable>
                        </Tooltip>
                        <View style={todoModalStyles.segmentedSeparator} />
                        <ScreenShareToggleButton
                            toolbarStyle={todoModalStyles.segmentedStopButton}
                            cropTargetRef={shareableRef}
                        />
                    </View>
                )
            } else {
                actions.push(
                    <View key="presenter" style={{ position: "relative" }}>
                        <ScreenShareToggleButton
                            ref={screenShareToggleRef}
                            toolbarStyle={todoModalStyles.toolbarButton}
                            cropTargetRef={shareableRef}
                        />
                        <View style={todoModalStyles.newBadge}>
                            <TextM
                                fontSize={7}
                                fontWeight="Bold"
                                color="#FFFFFF"
                            >
                                NEW
                            </TextM>
                        </View>
                    </View>
                )
            }
        }

        // Valider (if rejected)
        if (statusActivity === "rejected" && hasRole(props.user, "tutor")) {
            actions.push(
                <ToolbarButton
                    key="valider"
                    icon="check"
                    label="Valider"
                    onPress={() => showDialogValiderManuellement(item)}
                    color={theme.colors.success}
                    style={todoModalStyles.toolbarButton}
                />
            )
        }

        // Redonner (if finished - not todo)
        if (!interviewMode && !!item?.finished_at) {
            actions.push(
                <ToolbarButton
                    key="redonner"
                    icon="redo"
                    label="Redonner"
                    onPress={onPress}
                    loading={
                        props.activityTodoAsStudentRequest ||
                        props.activityTodoAsTutorRequest
                    }
                    style={todoModalStyles.toolbarButton}
                />
            )
        }

        return actions
    }

    // Menu items for secondary actions
    const renderMenuItems = () => {
        const items = []

        // Note : en mode brouillon, ce menu n'est pas rendu du tout (le kebab
        // lui-même est masqué plus bas), donc aucune garde ici — une garde
        // morte se relit comme une garde nécessaire et se recopie.
        if (fromArchived) {
            items.push(
                <Menu.Item
                    key="restaurer"
                    onPress={() => {
                        setMenuVisible(false)
                        onRestorePress()
                    }}
                    title="Restaurer"
                    leadingIcon="undo"
                />
            )
        } else {
            items.push(
                <Menu.Item
                    key="archiver"
                    onPress={() => {
                        setMenuVisible(false)
                        onDeletePress()
                    }}
                    title="Archiver"
                    leadingIcon="archive"
                />
            )
            if (!item?.finished_at && !props.isParentMode) {
                items.push(
                    <Menu.Item
                        key="modifier-titre"
                        onPress={() => {
                            setMenuVisible(false)
                            setTitleEditModalVisible(true)
                        }}
                        title="Modifier le titre"
                        leadingIcon="pencil"
                    />
                )
            }
        }

        return items
    }

    // Calculate modal dimensions first
    const modalWidth =
        width ??
        (dimensions.width > 800 ? dimensions.width : dimensions.width - 20)
    const modalHeight = height ?? "100%"

    const logImages = useMemo(
        () =>
            item?.activity_logs?.filter(
                (al) => al?.step === 2 && al?.action === "step_media_sent"
            ),
        [item?.activity_logs]
    )
    const logAudio = logImages?.[0]
    const logAudioChrono = item?.activity_logs?.find(
        (al) => al?.step === 2 && al?.action === "step_finished"
    )

    const correctionImage = item?.correction_image

    // Build images array with correction image first (if available).
    // Mémoïsé : ces tableaux partent en `data` de la FlatList du visionneur, et
    // une identité neuve à chaque rendu lui fait re-rendre toutes ses pages —
    // donc re-émettre des évènements de défilement, qui re-rendent ici.
    const workImages = useMemo(
        () => logImages?.map((x) => x?.work_attachment) || [],
        [logImages]
    )
    const images = useMemo(
        () => (correctionImage ? [correctionImage, ...workImages] : workImages),
        [correctionImage, workImages]
    )

    const hasPrerequisiteQuiz = item?.has_prerequisite_quiz
    const hasRendu = item?.media_count > 0
    const canShowSplit = modalWidth >= 800 || interviewMode

    // Determine available display modes - ORDER: visual_explanation, quiz, material, work_attachments, split
    const materialSkills = item?.material?.material_skills
    const hasMaterialSkills =
        typeof materialSkills === "string"
            ? materialSkills.trim().length > 0
            : Array.isArray(materialSkills) && materialSkills.length > 0
    const hasVisualExplanation =
        props.isLiveMode &&
        noModal &&
        hasRole(props.user, "tutor") &&
        hasMaterialSkills &&
        ((item?.material?.statement &&
            item?.material?.statement?.trim().length > 0) ||
            (item?.material?.transcript &&
                item?.material?.transcript?.trim().length > 0))

    const activityDisplayModes = []
    if (hasVisualExplanation) activityDisplayModes.push("visual_explanation")
    if (hasPrerequisiteQuiz) activityDisplayModes.push("quiz")
    activityDisplayModes.push("material")
    if (hasRendu) activityDisplayModes.push("work_attachments")
    if (hasRendu && canShowSplit) activityDisplayModes.push("split")

    const activityDisplayModesLabels = {
        quiz: "Quizz",
        material: "Énoncé",
        work_attachments: correctionImage ? "Rendu + correction" : "Rendu",
        split: "Énoncé + Rendu",
        visual_explanation: "Tableau blanc",
    }

    const activityDisplayModesIcons = {
        quiz: "clipboard-list",
        material: "file-alt",
        work_attachments: "image",
        split: "columns",
        visual_explanation: "chalkboard-teacher",
    }

    // Calculate default mode based on new rules:
    // 1. If has rendu → split (or work_attachments if split not available)
    // 2. Else if has quiz → quiz
    // 3. Else → material
    const getDefaultMode = () => {
        if (hasRendu) {
            return canShowSplit ? "split" : "work_attachments"
        }
        if (hasPrerequisiteQuiz) {
            return "quiz"
        }
        return "material"
    }

    const defaultMode = getDefaultMode()
    const defaultModeIndex = activityDisplayModes.indexOf(defaultMode)

    const [activityDisplayModeIndex, setActivityDisplayModeIndex] = useState(
        defaultModeIndex >= 0 ? defaultModeIndex : 0
    )

    // Keyboard shortcuts for annotation tools (web only, during screen share)
    const displayModesRef = useRef(activityDisplayModes)
    displayModesRef.current = activityDisplayModes
    const displayModeIndexRef = useRef(activityDisplayModeIndex)
    displayModeIndexRef.current = activityDisplayModeIndex

    useEffect(() => {
        if (!IS_WEB || !isFocusShareActive) return

        const handleKeyDown = (e) => {
            // Don't intercept shortcuts when typing in an input/textarea
            if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA")
                return

            if (e.key === "p" || e.key === "P") {
                e.preventDefault()
                setIsDrawing(true)
            } else if (
                e.key === "z" &&
                (e.ctrlKey || e.metaKey) &&
                !e.shiftKey
            ) {
                e.preventDefault()
                const mode =
                    displayModesRef.current[displayModeIndexRef.current]
                const canvas =
                    mode === "visual_explanation"
                        ? whiteboardCanvasRef.current
                        : mode === "split"
                          ? lastActiveCanvasRef.current
                          : mode === "work_attachments"
                            ? imageActiveCanvasRef.current
                            : materialCanvasRef.current
                canvas?.undo()
                setStrokeCount(getActivityStrokeCount())
            } else if (e.key === "Delete") {
                e.preventDefault()
                const mode =
                    displayModesRef.current[displayModeIndexRef.current]
                if (mode === "visual_explanation") {
                    whiteboardCanvasRef.current?.clearAll()
                } else if (mode === "split") {
                    materialCanvasRef.current?.clearAll()
                    imageActiveCanvasRef.current?.clearAll()
                } else if (mode === "work_attachments") {
                    imageActiveCanvasRef.current?.clearAll()
                } else {
                    materialCanvasRef.current?.clearAll()
                }
                setStrokeCount(getActivityStrokeCount())
            } else if (e.key === "Escape") {
                setIsDrawing(false)
            }
        }

        document.addEventListener("keydown", handleKeyDown)
        return () => {
            document.removeEventListener("keydown", handleKeyDown)
        }
    }, [isFocusShareActive])

    const prevMediaCountRef = useRef(item?.media_count)

    // Reset display mode when activity changes
    useEffect(() => {
        if (prevItemIdRef.current !== item?.id) {
            prevItemIdRef.current = item?.id
            prevMediaCountRef.current = item?.media_count
            const newDefaultIndex =
                activityDisplayModes.indexOf(getDefaultMode())
            setActivityDisplayModeIndex(
                newDefaultIndex >= 0 ? newDefaultIndex : 0
            )
        }
    }, [item?.id, hasRendu, hasPrerequisiteQuiz, canShowSplit])

    // « Retirer » takes the Quizz tab out of the same activity: the tab shown is
    // kept by its name (the quiz's gives way to the statement), not by its
    // position, which now points one tab further — or past the last one.
    const shownTabRef = useRef(null)
    useEffect(() => {
        const before = shownTabRef.current
        shownTabRef.current = {
            id: item?.id,
            hasQuiz: hasPrerequisiteQuiz,
            mode: activityDisplayModes[activityDisplayModeIndex],
        }
        if (!before || before.id !== item?.id || !before.hasQuiz || hasPrerequisiteQuiz)
            return
        const kept = activityDisplayModes.indexOf(
            before.mode === "quiz" ? "material" : before.mode
        )
        if (kept >= 0) setActivityDisplayModeIndex(kept)
    })

    // Switch to split/rendu view when student submits a photo
    useEffect(() => {
        if (item?.media_count > (prevMediaCountRef.current || 0)) {
            prevMediaCountRef.current = item?.media_count
            // Switch to split if available, otherwise work_attachments
            const splitIndex = activityDisplayModes.indexOf("split")
            const renduIndex = activityDisplayModes.indexOf("work_attachments")
            if (splitIndex >= 0) {
                setActivityDisplayModeIndex(splitIndex)
            } else if (renduIndex >= 0) {
                setActivityDisplayModeIndex(renduIndex)
            }
        }
    }, [item?.media_count])

    // Structured correction is shown to the tutor under the (unchanged) image,
    // whenever the correction completed and carries points — no confidence gate.
    // Hidden during screen share / interview so the annotation flow is untouched.
    const correctionPoints = item?.correction_points
    const showStructuredCorrection =
        isTutorViewer &&
        !interviewMode &&
        !isFocusShareActive &&
        correctionPoints?.length > 0

    // Interactive mode: the front renders the markers itself over the RAW
    // copy pages (bubbles kept off the writing, eye-toggleable, tappable, in
    // sync with the analysis cards). Requires every point to carry a usable
    // anchor — otherwise fall back to the baked annotated image (legacy
    // corrections generated before anchors were exposed).
    const anchoredCorrection =
        showStructuredCorrection &&
        workImages?.length > 0 &&
        correctionPoints.every(hasUsableAnchor)

    // With interactive markers the baked image is redundant — the carousel
    // shows the raw copy pages only. Fallback keeps the baked image first.
    const displayImages = anchoredCorrection ? workImages : images

    const renderCorrectionMarkers = (index) =>
        anchoredCorrection ? (
            <CorrectionMarkersOverlay
                points={correctionPoints}
                pageIndex={index}
                pagesCount={workImages.length}
                imageUri={workImages[index]}
                visible={annotationsVisible}
                selectedIndex={selectedPointIndex}
                onSelectPoint={handleSelectPointFromMarker}
            />
        ) : null

    const renderAnnotationsToggle = () =>
        anchoredCorrection ? (
            <IconButtonM
                size={30}
                icon={annotationsVisible ? "eye-slash" : "eye"}
                iconColor={theme.colors.primary}
                onPress={() => setAnnotationsVisible((v) => !v)}
            />
        ) : null

    // Discoverability pill for the full-height copy view ("Énoncé + Rendu"):
    // when the bottom panel is closed, a "N points" badge next to the image
    // controls signals the tappable bubbles and opens the panel on point ①.
    const firstPointIndex = correctionPoints?.length
        ? Math.min(...correctionPoints.map((p) => p?.index ?? Infinity))
        : null
    const renderPointsBadge = () =>
        anchoredCorrection && selectedPointIndex == null ? (
            <TouchableRippleM
                onPress={() => handleSelectPointFromCard(firstPointIndex)}
                style={{
                    borderWidth: 1.5,
                    borderColor: theme.colors.primary,
                    borderRadius: 16,
                    paddingVertical: 4,
                    paddingHorizontal: 12,
                    marginRight: 2,
                }}
            >
                <TextM fontSize={12} fontWeight="Bold" color={theme.colors.primary}>
                    {correctionPoints.length} point
                    {correctionPoints.length > 1 ? "s" : ""}
                </TextM>
            </TouchableRippleM>
        ) : null

    const withModal = (withModal, Component) => {
        if (withModal) {
            return (
                <ModalM
                    visible={isOpen}
                    onDismiss={handleShowModal}
                    style={{
                        width: modalWidth,
                        height: "90%",
                    }}
                >
                    {Component}
                </ModalM>
            )
        } else {
            return Component
        }
    }

    return withModal(
        !noModal,
        <View
            style={{
                width: modalWidth,
                height: modalHeight,
            }}
        >
            {/* Header: Close + Title/info + Actions */}
            {!interviewMode && (
                <View style={todoModalStyles.header}>
                    {/* Left: Close button + Activity info */}
                    <View style={todoModalStyles.headerLeft}>
                        <IconButton
                            icon="times"
                            iconColor={theme.colors.primary}
                            size={28}
                            style={{ margin: 0 }}
                            onPress={
                                onRequestClose || onExit || handleShowModal
                            }
                        />
                        <ActivityItemInfo
                            item={item}
                            schoolSubject={schoolSubject}
                            status={status}
                            statusActivity={statusActivity}
                            subtitleExtra={
                                isTutorViewer && !draftMode ? (
                                    <QuizFirstMention
                                        activity={item}
                                        studentId={studentId}
                                        studentName={student?.firstname}
                                        liveSessionId={
                                            props.isLiveMode
                                                ? props.liveSessionId
                                                : null
                                        }
                                    />
                                ) : null
                            }
                        />
                    </View>
                    {/* Vertical separator */}
                    <View style={todoModalStyles.headerSeparator} />
                    {/* Right: Action buttons */}
                    <View style={todoModalStyles.headerRight}>
                        {/* Show avatar if student is viewing this activity,
                            or unconditionally in draft mode (Sam preview)
                            so the tutor sees who they're prescribing for. */}
                        {!fromArchived &&
                            student &&
                            ((isOnline && currentStudentActivityId === item?.id) ||
                                draftMode) && (
                                <AvatarM
                                    user={student}
                                    size={28}
                                    forceBorder
                                    onboardingMode
                                    style={{ marginRight: 4 }}
                                />
                            )}
                        {/* Action buttons */}
                        {renderHeaderActions()}
                        {/* Menu for secondary actions — hidden in draft mode
                            since all menu items rely on a persisted activity id. */}
                        {!draftMode && (
                            <Menu
                                visible={menuVisible}
                                onDismiss={() => setMenuVisible(false)}
                                anchor={
                                    <Pressable
                                        onPress={() => setMenuVisible(true)}
                                        style={({ pressed }) => [
                                            todoModalStyles.toolbarButton,
                                            { opacity: pressed ? 0.7 : 1 },
                                        ]}
                                    >
                                        <FontAwesome5
                                            name="ellipsis-v"
                                            size={14}
                                            color={theme.colors.greyAlt}
                                        />
                                    </Pressable>
                                }
                                contentStyle={{
                                    backgroundColor: theme.colors.white,
                                }}
                            >
                                {renderMenuItems()}
                            </Menu>
                        )}
                    </View>
                </View>
            )}
            {/* Shareable zone: tabs + content (cropped by Region Capture) */}
            <View ref={shareableRef} style={{ flex: 1 }}>
                {/* View Switcher - Full Width Tabs */}
                {!interviewMode && activityDisplayModes.length > 1 && (
                    <View style={todoModalStyles.tabsContainer}>
                        {activityDisplayModes.map((mode, index) => {
                            const isActive = activityDisplayModeIndex === index
                            return (
                                <Pressable
                                    key={mode}
                                    onPress={() => {
                                        setActivityDisplayModeIndex(index)
                                        if (
                                            mode === "visual_explanation" &&
                                            !isFocusShareActive
                                        ) {
                                            screenShareToggleRef.current?.startScreenShare()
                                        }
                                    }}
                                    style={[
                                        todoModalStyles.tab,
                                        isActive && todoModalStyles.tabActive,
                                    ]}
                                >
                                    <FontAwesome5
                                        name={activityDisplayModesIcons[mode]}
                                        size={12}
                                        color={
                                            isActive
                                                ? theme.colors.primary
                                                : theme.colors.grey
                                        }
                                        style={{ marginRight: 6 }}
                                    />
                                    <TextM
                                        fontSize={12}
                                        fontWeight={
                                            isActive ? "SemiBold" : "Regular"
                                        }
                                        color={
                                            isActive
                                                ? theme.colors.primary
                                                : theme.colors.grey
                                        }
                                    >
                                        {activityDisplayModesLabels[mode]}
                                    </TextM>
                                </Pressable>
                            )
                        })}
                    </View>
                )}
                {/* Hint banner: shown on whiteboard tab when screen share is not active */}
                {activityDisplayModes[activityDisplayModeIndex] ===
                    "visual_explanation" &&
                    !isFocusShareActive &&
                    !shareHintDismissed && (
                        <View style={todoModalStyles.shareHintBanner}>
                            <FontAwesome5
                                name="info-circle"
                                size={16}
                                color={theme.colors.primary}
                                style={{ marginRight: 10 }}
                            />
                            <TextM
                                fontSize={13}
                                color={theme.colors.primary}
                                style={{ flex: 1, marginRight: 10 }}
                                wrap
                            >
                                Clique sur{" "}
                                <TextM
                                    fontSize={13}
                                    fontWeight="Bold"
                                    color={theme.colors.primary}
                                >
                                    Présenter
                                </TextM>{" "}
                                pour partager ton écran avec l'élève
                            </TextM>
                            <IconButton
                                icon="times"
                                size={18}
                                iconColor={theme.colors.primary}
                                onPress={() => setShareHintDismissed(true)}
                                style={{ margin: 0, marginLeft: "auto" }}
                            />
                        </View>
                    )}
                <View
                    style={{
                        flex: 1,
                        flexDirection: "row",
                        alignItems: "stretch",
                        backgroundColor: theme.colors.lighter,
                        position: "relative",
                    }}
                    onLayout={(e) => {
                        const { width: w, height: h } = e.nativeEvent.layout
                        if (w > 0 && h > 0) {
                            setContentLayout((prev) => {
                                if (prev.width === w && prev.height === h)
                                    return prev
                                return { width: w, height: h }
                            })
                        }
                    }}
                >
                    {["split", "material"]?.includes(
                        activityDisplayModes[activityDisplayModeIndex]
                    ) && (
                        <View style={{ flex: 1, position: "relative" }}>
                            {interviewMode ? (
                                <Pressable
                                    onPress={() => {
                                        setMaterial(item?.material)
                                        handleShowMaterialModal()
                                    }}
                                    style={{
                                        flex: 1,
                                        position: "relative",
                                    }}
                                >
                                    <MaterialModal
                                        material={item?.material}
                                        noModal
                                        style={{
                                            overflow: "hidden",
                                            marginBottom: 0,
                                            width: "100%",
                                        }}
                                        isSplit={
                                            activityDisplayModes[
                                                activityDisplayModeIndex
                                            ] === "split"
                                        }
                                        annotationDrawing={
                                            isFocusShareActive && isDrawing
                                        }
                                        annotationOverlay={
                                            isFocusShareActive ? (
                                                <AnnotationCanvas
                                                    ref={materialCanvasRef}
                                                    width={
                                                        activityDisplayModes[
                                                            activityDisplayModeIndex
                                                        ] === "split"
                                                            ? contentLayout.width /
                                                              2
                                                            : contentLayout.width
                                                    }
                                                    height={
                                                        contentLayout.height
                                                    }
                                                    color="#5CE0D8"
                                                    strokeWidth={4}
                                                    enabled={isDrawing}
                                                    strokesRef={getStrokesRef(
                                                        "material"
                                                    )}
                                                    onStrokeEnd={() => {
                                                        lastActiveCanvasRef.current =
                                                            materialCanvasRef.current
                                                        setStrokeCount(
                                                            (s) => s + 1
                                                        )
                                                    }}
                                                />
                                            ) : null
                                        }
                                    />
                                </Pressable>
                            ) : (
                                <MaterialModal
                                    material={item?.material}
                                    noModal
                                    style={{
                                        overflow: "hidden",
                                        marginBottom: 0,
                                        width: "100%",
                                        flex: 1,
                                        position: "relative",
                                    }}
                                    isSplit={
                                        activityDisplayModes[
                                            activityDisplayModeIndex
                                        ] === "split"
                                    }
                                    annotationDrawing={
                                        isFocusShareActive && isDrawing
                                    }
                                    annotationOverlay={
                                        isFocusShareActive ? (
                                            <AnnotationCanvas
                                                ref={materialCanvasRef}
                                                width={
                                                    activityDisplayModes[
                                                        activityDisplayModeIndex
                                                    ] === "split"
                                                        ? contentLayout.width /
                                                          2
                                                        : contentLayout.width
                                                }
                                                height={contentLayout.height}
                                                color="#5CE0D8"
                                                strokeWidth={4}
                                                enabled={isDrawing}
                                                strokesRef={getStrokesRef(
                                                    "material"
                                                )}
                                                onStrokeEnd={() => {
                                                    lastActiveCanvasRef.current =
                                                        materialCanvasRef.current
                                                    setStrokeCount((s) => s + 1)
                                                }}
                                            />
                                        ) : null
                                    }
                                />
                            )}
                        </View>
                    )}
                    {activityDisplayModes[activityDisplayModeIndex] ===
                        "quiz" && (
                        <View style={{ flex: 1 }}>
                            <PrerequisiteQuizDisplay
                                activity={item}
                                quizFeedback={quizFeedback}
                                onQuizFeedback={handleQuizFeedback}
                            />
                        </View>
                    )}
                    {["split", "work_attachments"]?.includes(
                        activityDisplayModes[activityDisplayModeIndex]
                    ) && (
                        <View
                            style={{ flex: 1, position: "relative" }}
                            onLayout={(e) => {
                                const { height: h } = e.nativeEvent.layout
                                if (h > 0 && h !== modeContentHeight) {
                                    setModeContentHeight(h)
                                }
                            }}
                        >
                            {/* "Rendu + correction": student's raw work on the left,
                                the correction (image + analysis) on the right. */}
                            {item?.category === "write" &&
                            activityDisplayModes[activityDisplayModeIndex] ===
                                "work_attachments" &&
                            showStructuredCorrection &&
                            correctionImage ? (
                                <View style={{ flex: 1, flexDirection: "row" }}>
                                    {/* Left (~62%): the copy is the master view —
                                        raw pages with interactive markers when
                                        anchors are available, otherwise the baked
                                        annotated image first. Right: the analysis,
                                        full height, synced with the markers. */}
                                    <View style={{ flex: 1.6, minHeight: 0 }}>
                                        <ImageViewer
                                            imageIndex={
                                                anchoredCorrection
                                                    ? viewerImageIndex
                                                    : null
                                            }
                                            onIndexChange={
                                                anchoredCorrection
                                                    ? setViewerImageIndex
                                                    : undefined
                                            }
                                            images={displayImages}
                                            close={null}
                                            withChrono={false}
                                            clearMode
                                            scrollToStart={!!correctionImage}
                                            aboveControlsComponent={null}
                                            width={(modalWidth * 1.6) / 2.6}
                                            renderImageOverlay={
                                                renderCorrectionMarkers
                                            }
                                            extraControlComponent={renderAnnotationsToggle()}
                                        />
                                    </View>
                                    <View
                                        style={{
                                            flex: 1,
                                            minHeight: 0,
                                            borderLeftWidth: 1,
                                            borderLeftColor:
                                                theme.colors.lightMore,
                                        }}
                                    >
                                        {renderCorrectionAnalysisPanel()}
                                    </View>
                                </View>
                            ) : (
                            <>
                            {/* minHeight:0 lets the image pane shrink on RN-web
                                (CSS min-height:auto would otherwise pin it) so the
                                correction panel below gets its space. Interactive
                                (anchored) corrections keep the copy FULL height —
                                the points panel expands on demand underneath;
                                legacy corrections keep the always-visible list
                                (their markers aren't tappable), copy at 1.15/1. */}
                            <View
                                style={{
                                    flex:
                                        showStructuredCorrection &&
                                        !anchoredCorrection
                                            ? 1.15
                                            : 1,
                                    minHeight: 0,
                                }}
                            >
                            {item?.category === "write" ? (
                                interviewMode ? (
                                    <Pressable
                                        style={{
                                            flex: 1,
                                            position: "relative",
                                        }}
                                        onPress={() => {
                                            //? Hacky solution to display the image in the modal
                                            setMaterial({
                                                attachments: [
                                                    images[
                                                        activityDisplayModeIndex
                                                    ],
                                                ],
                                            })
                                            handleShowMaterialModal()
                                        }}
                                    >
                                        <ImageViewer
                                            imageIndex={null}
                                            images={images}
                                            close={null}
                                            withChrono={false}
                                            clearMode
                                            scrollToStart={!!correctionImage}
                                            width={
                                                activityDisplayModes[
                                                    activityDisplayModeIndex
                                                ] === "split"
                                                    ? modalWidth / 2
                                                    : modalWidth
                                            }
                                            {...(isFocusShareActive
                                                ? {
                                                      annotationEnabled:
                                                          isDrawing,
                                                      annotationColor:
                                                          "#5CE0D8",
                                                      annotationStrokeWidth: 4,
                                                      getStrokesRef:
                                                          getImageStrokesRef,
                                                      onStrokeEnd: () => {
                                                          lastActiveCanvasRef.current =
                                                              imageActiveCanvasRef.current
                                                          setStrokeCount(
                                                              (s) => s + 1
                                                          )
                                                      },
                                                      activeCanvasRef:
                                                          imageActiveCanvasRef,
                                                  }
                                                : {})}
                                        />
                                    </Pressable>
                                ) : (
                                    <ImageViewer
                                        imageIndex={
                                            anchoredCorrection
                                                ? viewerImageIndex
                                                : null
                                        }
                                        onIndexChange={
                                            anchoredCorrection
                                                ? setViewerImageIndex
                                                : undefined
                                        }
                                        images={displayImages}
                                        close={null}
                                        withChrono={false}
                                        clearMode
                                        scrollToStart={!!correctionImage}
                                        width={
                                            activityDisplayModes[
                                                activityDisplayModeIndex
                                            ] === "split"
                                                ? modalWidth / 2
                                                : modalWidth
                                        }
                                        aboveControlsComponent={null}
                                        renderImageOverlay={
                                            renderCorrectionMarkers
                                        }
                                        extraControlComponent={
                                            anchoredCorrection ? (
                                                <View
                                                    style={{
                                                        flexDirection: "row",
                                                        alignItems: "center",
                                                    }}
                                                >
                                                    {renderPointsBadge()}
                                                    {renderAnnotationsToggle()}
                                                </View>
                                            ) : null
                                        }
                                        {...(isFocusShareActive
                                            ? {
                                                  annotationEnabled: isDrawing,
                                                  annotationColor: "#5CE0D8",
                                                  annotationStrokeWidth: 4,
                                                  getStrokesRef:
                                                      getImageStrokesRef,
                                                  onStrokeEnd: () => {
                                                      lastActiveCanvasRef.current =
                                                          imageActiveCanvasRef.current
                                                      setStrokeCount(
                                                          (s) => s + 1
                                                      )
                                                  },
                                                  activeCanvasRef:
                                                      imageActiveCanvasRef,
                                              }
                                            : {})}
                                    />
                                )
                            ) : (
                                <AudioRecordingContainer
                                    recordingURI={cleanMediaUrl(
                                        logAudio?.work_attachment
                                    )}
                                    duration={
                                        logAudioChrono?.step_data
                                            ?.chrono_duration
                                    }
                                    readOnly
                                />
                            )}
                            </View>
                            {showStructuredCorrection &&
                                (anchoredCorrection ? (
                                    // Énoncé + Rendu: contextual bottom panel —
                                    // expands on bubble/badge tap, one card at a
                                    // time (horizontal carousel), ✕ restores the
                                    // full-height copy.
                                    <CorrectionPointsPanel
                                        points={correctionPoints}
                                        selectedIndex={selectedPointIndex}
                                        onSelectPoint={
                                            handleSelectPointFromCard
                                        }
                                        onClose={() =>
                                            setSelectedPointIndex(null)
                                        }
                                        height={pointsPanelHeight}
                                        actions={renderCorrectionFeedback(
                                            true
                                        )}
                                    />
                                ) : (
                                    // Legacy (no anchors): always-visible
                                    // vertical analysis below the image.
                                    renderCorrectionAnalysisPanel()
                                ))}
                            </>
                            )}
                        </View>
                    )}
                    {activityDisplayModes[activityDisplayModeIndex] ===
                        "visual_explanation" && (
                        <WhiteboardTab
                            material={item?.material}
                            user={props.user}
                            isFocusShareActive={isFocusShareActive}
                            isDrawing={isDrawing}
                            contentLayout={contentLayout}
                            whiteboardCanvasRef={whiteboardCanvasRef}
                            getStrokesRef={getStrokesRef}
                            lastActiveCanvasRef={lastActiveCanvasRef}
                            setStrokeCount={setStrokeCount}
                        />
                    )}
                </View>
            </View>
            <MaterialModal
                handleShowModal={handleShowMaterialModal}
                isOpen={isMaterialOpen}
                material={material}
            />
            <ParcoursChoiceModal
                visible={showParcoursChoice}
                onDismiss={() => setShowParcoursChoice(false)}
                studentId={studentId}
                student={student}
                user={props.user}
                materialId={item?.material?.id}
                sourceLevel={item?.material?.level}
                sourceTitle={item?.material?.title}
                sourceConfidence={item?.material?.structured_data?.analysis?.confidence}
                sourceSubjectId={item?.material?.school_subject_id}
                sourceClass={item?.material?.class}
                schoolSubjects={props.schoolSubjects}
                apiRequest={props.apiRequest}
                isLiveMode={props.isLiveMode}
                navigation={navigation}
            />

            <EditActivityTitleModal
                isVisible={isTitleEditModalVisible}
                onClose={() => setTitleEditModalVisible(false)}
                activity={item}
                apiRequest={props.apiRequest}
            />
        </View>
    )
}

const todoModalStyles = StyleSheet.create({
    header: {
        flexDirection: "row",
        alignItems: "center",
        paddingVertical: 8,
        paddingRight: 4,
    },
    headerLeft: {
        flexDirection: "row",
        alignItems: "center",
        flex: 1,
    },
    headerSeparator: {
        width: 1,
        height: 36,
        backgroundColor: theme.colors.lighter,
        marginHorizontal: 8,
    },
    headerRight: {
        flexDirection: "row",
        alignItems: "center",
        flexShrink: 1,
        flexWrap: "wrap",
        justifyContent: "flex-end",
    },
    // Toolbar button style (vertical: icon on top, label below)
    toolbarButton: {
        alignItems: "center",
        justifyContent: "center",
        paddingVertical: 6,
        paddingHorizontal: 10,
        minWidth: 50,
        height: 44,
        backgroundColor: theme.colors.whiteAlmost,
        borderRadius: 8,
        marginHorizontal: 2,
    },
    // Segmented group for screen share tools
    segmentedGroup: {
        flexDirection: "row",
        alignItems: "center",
        backgroundColor: theme.colors.whiteAlmost,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: theme.colors.error + "40",
        marginHorizontal: 2,
        height: 44,
        overflow: "hidden",
    },
    segmentedButton: {
        alignItems: "center",
        justifyContent: "center",
        width: 38,
        height: 44,
    },
    segmentedSeparator: {
        width: 1,
        height: 24,
        backgroundColor: theme.colors.lightMore,
    },
    segmentedStopButton: {
        alignItems: "center",
        justifyContent: "center",
        paddingVertical: 4,
        paddingHorizontal: 10,
        height: 44,
        backgroundColor: theme.colors.error + "12",
    },
    // Tabs styles (full width)
    tabsContainer: {
        flexDirection: "row",
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.lighter,
    },
    tab: {
        flex: 1,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        paddingVertical: 10,
        borderBottomWidth: 2,
        borderBottomColor: "transparent",
    },
    tabActive: {
        borderBottomColor: theme.colors.primary,
    },
    shareHintBanner: {
        flexDirection: "row",
        alignItems: "center",
        backgroundColor: theme.colors.primaryLighter || "#E3F2FD",
        borderRadius: 8,
        padding: 12,
        marginHorizontal: 10,
        marginTop: 10,
        borderLeftWidth: 3,
        borderLeftColor: theme.colors.primary,
    },
    newBadge: {
        position: "absolute",
        top: -2,
        right: 0,
        backgroundColor: theme.colors.success,
        borderRadius: 4,
        paddingHorizontal: 3,
        paddingVertical: 0,
        zIndex: 1,
    },
})

const mapStateToProps = (state) => {
    return {
        user: state.data.currentUser?.object,
        participants: state.liveMode?.liveParticipants,
        liveSessionId: state.liveMode?.liveSessionId,
        activityTemplates: state.data.staticData?.activity_templates,
        activityTodoAsStudentRequest: state.api.activityTodoAsStudent?.request,
        activityTodoAsTutorRequest: state.api.activityTodoAsTutor?.request,
        finalizeActivitySilentRequest:
            state.api.finalizeActivitySilent?.request,
        isLiveMode: state.liveMode.isLiveModeEnabled,
        isFocusScreenShare: state.liveMode?.isFocusScreenShare,
        schoolSubjects: state.data.staticData?.school_subjects,
        isParentMode: state.data.family?.isParentMode,
        ongoingCheckpointIds: state.liveMode?.ongoingCheckpointIds,
        pendingAttachmentsByActivity:
            state.data?.pendingAttachments?.byActivityId,
    }
}

const mapDispatchToProps = {
    apiRequest,
    apiIdle,
}

export default connect(mapStateToProps, mapDispatchToProps)(TodoModal)
